<script>
  import { extent } from 'd3-array';
  import { format } from 'date-fns';
  import { scaleBand } from 'd3-scale';
  import { scaleOrdinal } from 'd3-scale';
  import { dateParser, dateFormat } from '../../utils/d3-utils';
  import { intcomma, apmonth, apdate } from 'journalize';

  import { getSortByString, getSortByNumber } from '../../utils/sorters';

  // the hook for supplied props
  export let data;
  export let is_county;

  // filter out districts with 0 enrollment
  data = data.filter((d) => d.enr_total > 0);

  const availableCounties = Array.from(
    new Set(data.map((d) => d.county))
  ).sort();

  let selectedCounty = is_county? data[0].county :'Los Angeles';

  // Table row limit
  const limit = 10;
  let showAll = false;

  // computed values
  const getSortText = (t) => {
    if (t.includes(':')) {
      return t.split(':')[1].trim();
    } else {
      return t.trim();
    }
  };

  let headers = [
    { text: 'District', class: 'text-left sortable district_name', attr: 'district'},
    {text: 'Elementary', class:'text-left left-border status', attr: 'elem_instruction_status', },
    {text: 'Secondary', class:'text-left left-border status', attr: 'sec_instruction_status', },
    {text: 'Enrollment', class:'text-right left-border sortable desktop-only',attr:'enr_total'},
  ];

  let crosswalk = {

  }

  let type = 'enr_total';
  let sortAttr = 'enr_total';
  let sortAscending = false;
  let sortFunctions = {
    district: {
      true: getSortByString('district', true),
      false: getSortByString('district', false),
    },
    enr_total: {
      true: getSortByNumber('enr_total', true),
      false: getSortByNumber('enr_total', false),
    },    
  };


  let preppedData = data.map((d) => {
    d.search_text = d.district.toUpperCase();
    d.sort_text = getSortText(d.district);
    return d;
  });

  $: selectedCounties = preppedData.filter((d) => d.county === selectedCounty);
  $: filteredData = selectedCounties;
  $: tableRows = filteredData.sort(sortFunctions[sortAttr][sortAscending]);
  $: enoughRows = tableRows.length > limit;

  let colors = [
    '#ffffff',
    '#1281aacc',//inperson blue
    '#83c6e0cc',//hybrid light blue
    '#d36627cc', //distance orange
  ];

  const colorFunctions = data.reduce((obj, v) => {
    const values = [null, 'Open for Full In-Person Instruction','Open for Hybrid Instruction','Not Open for In-Person Instruction'];
    const scale = scaleOrdinal(values, colors);
    obj[v.district] = scale;    
    return obj;
  }, {});

  const hatch = "linear-gradient(45deg, #1281aacc 25%, #fff 25%, #fff 50%, #1281aacc 50%, #1281aacc 75%, #fff 75%, #fff 100%)"

  // Filter table
  function filterRows(e) {
    showAll = false;
    const value = e.target.value.toUpperCase();
    if (value == '') {
      filteredData = selectedCounties;
    } else {
      filteredData = selectedCounties.filter(
        (d) => d.search_text.indexOf(value) > -1
      );
    }
  }

  // flip the toggle on each click
  function onToggle() {
    showAll = !showAll;
  }

  // Filter table
  function onSort(e) {

    const thisAttr = e.target.getAttribute('data-attr');

    const sortableAttrs = Object.keys( sortFunctions );

    if (sortableAttrs.includes(thisAttr)){
      if (thisAttr === sortAttr) {
        sortAscending = !sortAscending;
      } else {
        sortAttr = thisAttr;
        sortAscending = false;
      }
    }
  }

  const onChange = () => {};


</script>

<div>
  <div class="dg-table">
    <div class="dg-table--inputs button-grid">
      {#if is_county == false}
      <div class="button-grid-group">
        <label for="school-table-select">Filter by county</label>
        <select
          id="school-table-select"
          name="counties"
          bind:value={selectedCounty}
          on:blur={onChange}
        >
          {#each availableCounties as value}
            <option {value}>{value}</option>
          {/each}
        </select>
      </div>
      {/if}
      <div class="button-grid-group">
        <label for="school-table-input">Search by name</label>
        <input
          class="search"
          style="display: block;"
          id="school-table-input"
          on:keyup={filterRows}
        />
      </div>
    </div>
  </div>
    <div class="table-key">
      <span style="background-color: #1281aacc;"></span>In-person 
      <span style="background-color: #83c6e0cc;"></span>Hybrid 
      <span style="background-color: #d36627cc;"></span> Distance 
    </div>
  <table class="dg-table">
    <thead>
      <tr>
        {#each headers as obj}
          <th
            scope="col"
            data-attr={obj.attr}
            class={obj.class}
            class:sort-asc={sortAttr === obj.attr && sortAscending}
            class:sort-desc={sortAttr === obj.attr && !sortAscending}
            on:click={onSort}
            style={obj.style}
          >
            {obj.text}
          </th>
        {/each}
      </tr>
    </thead>
    <tbody>
      {#each tableRows as obj, idx}
        <tr class:display-none={idx + 1 > limit && !showAll}>
          <th scope="row" data-fips={obj.fips} class="district-name text-left">
              {obj.district} {#if obj.reopening_plan}<a href="{obj.reopening_plan}"><i class="LATLinkOutArrow"></i></a>{/if}
              <span class="details">
                {#if obj.update_date}As of {dateFormat(obj.update_date)}{/if}
              </span>
          </th>
          <td class="text-left left-border" style="background:{colorFunctions[obj.district](obj.elem_instruction_status) };"
           aria-label="{obj.elem_instruction_status}"
          >
          </td>
          <td class="text-left left-border"
            style="background: {colorFunctions[obj.district](obj.sec_instruction_status)}"
            aria-label="{obj.sec_instruction_status}"
          >
          </td>
          <td class="text-right left-border lighter desktop-only">
            {intcomma(obj.enr_total)}
          </td>      
        </tr>
      {/each}
    </tbody>
  </table>
  <div class="text-center">
    {#if enoughRows}
      <button class="btn" on:click={onToggle}>
        {showAll ? 'Show less' : 'Show all'}
      </button>
    {/if}
  </div>
</div>

<style>
  .table-key {
    display: flex;
    align-items: center;
    font-family: "Benton Gothic";
    font-size: 0.8em;
    justify-content: center;
    margin-bottom: 15px;
}
</style>
